import { ParsedQuery } from 'query-string/base';
import { matchPath, Params } from 'react-router-dom';
import { ApplicationContextDTO, EnvironmentDTO, FeatureFlag } from '../api/dto';
import { getRoute, ROUTES, RouteType } from './routes';

type RichRedirect = string | ((pathParams: Params, queryParams: ParsedQuery) => Parameters<typeof getRoute>[0]);

const DEFAULT_OIM_ROUTE = '/impact/secure';

const oimUrl =
    (place: string | ((pathParams: Params, queryParams: ParsedQuery) => string)): RichRedirect =>
    (pathParams, queryParams) => ({
        path: '/impact/secure/',
        queryParams: { place: typeof place === 'string' ? place : place(pathParams, queryParams) },
    });

export const ROUTES_TO_OIM: Record<RouteType, RichRedirect> = {
    // Warning: order matters, place the static routes first (e.g. `/new` before `/:id`)
    [ROUTES.homepage]: oimUrl('searchActivity'),
    [ROUTES.news]: DEFAULT_OIM_ROUTE,
    [ROUTES.activity.search]: oimUrl('searchActivity'),
    [ROUTES.activity.bulkActions]: oimUrl('searchActivity'),
    [ROUTES.activity.watchlist]: oimUrl('searchFavouriteActivity'),
    [ROUTES.activity.clusters]: oimUrl('cluster/searchActivityCluster'),
    [ROUTES.activity.scheduler]: oimUrl('activityScheduler'),
    [ROUTES.logbook]: oimUrl('onSiteComment/searchOnSiteComment'),
    [ROUTES.activity.create]: oimUrl('createActivity'),
    [ROUTES.activity.view]: oimUrl(({ id }) => `editActivity:${id}`),
    [ROUTES.activity.clone]: oimUrl(({ id }) => `editActivity:${id}`),
    [ROUTES.activity.createTemplate]: oimUrl('createActivity'),
    [ROUTES.activity.cloneFromTemplate]: oimUrl(({ id }) => `editActivity:${id}`),
    [ROUTES.activity.changeRequest.view]: DEFAULT_OIM_ROUTE,
    [ROUTES.dimr.search]: oimUrl('dimr/searchDimr'),
    [ROUTES.dimr.viewLatest]: oimUrl(({ id }) => `dimr/editDimr:${id}:1`), // Imperfect
    [ROUTES.dimr.view]: oimUrl(({ id }) => `dimr/editDimr:${id}:1`), // Imperfect
    [ROUTES.dimr.create]: DEFAULT_OIM_ROUTE,
    [ROUTES.wdp.create]: DEFAULT_OIM_ROUTE,
    [ROUTES.wdp.clone]: DEFAULT_OIM_ROUTE,
    [ROUTES.wdp.search]: oimUrl('searchWorkDosePlanning'),
    [ROUTES.wdp.view]: oimUrl(({ id }) => `dimr/editWorkDosePlanning:${id}:1`), //
    [ROUTES.wdp.viewLatest]: oimUrl(({ id }) => `dimr/editWorkDosePlanning:${id}:1`), //
    [ROUTES.vic.search]: oimUrl('safety/searchVic'),
    [ROUTES.vic.create]: DEFAULT_OIM_ROUTE,
    [ROUTES.vic.view]: oimUrl(({ id }) => `safety/editSafetyDocument:VIC:${id}`),
    [ROUTES.vic.clone]: DEFAULT_OIM_ROUTE,
    [ROUTES.lockout.search]: oimUrl('safety/searchLockout'),
    [ROUTES.lockout.view]: oimUrl(({ id }) => `safety/editSafetyDocument:LKO:${id}`),
    [ROUTES.lockout.create]: DEFAULT_OIM_ROUTE,
    [ROUTES.lockout.clone]: DEFAULT_OIM_ROUTE,
    [ROUTES.firePermit.search]: oimUrl('safety/searchFirePermit'),
    [ROUTES.firePermit.create]: DEFAULT_OIM_ROUTE,
    [ROUTES.firePermit.view]: oimUrl(({ id }) => `safety/editSafetyDocument:FPR:${id}`),
    [ROUTES.firePermit.clone]: DEFAULT_OIM_ROUTE,
    [ROUTES.is37.search]: oimUrl('safety/searchIS37'),
    [ROUTES.is37.create]: DEFAULT_OIM_ROUTE,
    [ROUTES.is37.view]: oimUrl(({ id }) => `safety/editSafetyDocument:IS37N:${id}`),
    [ROUTES.is37.clone]: DEFAULT_OIM_ROUTE,
    [ROUTES.noteDeCoupure.search]: DEFAULT_OIM_ROUTE,
    [ROUTES.noteDeCoupure.create]: DEFAULT_OIM_ROUTE,
    [ROUTES.noteDeCoupure.clone]: DEFAULT_OIM_ROUTE,
    [ROUTES.noteDeCoupure.view]: DEFAULT_OIM_ROUTE,
    [ROUTES.tasks]: oimUrl('user/showDashboard'),
    [ROUTES.radiationDoses]: oimUrl(
        ({}, { dimrId, activityId, activityClusterId }) =>
            `radiationDoseReports:${activityId ?? ''}:${dimrId ?? ''}:${activityClusterId ?? ''}`
    ),
    [ROUTES.accessControl]: oimUrl('user/searchAccessControl'),
    [ROUTES.locations]: '/impact/secure/locationList',
    [ROUTES.professionalVisits]: '/impact/secure/dataReport/professionalVisits',
    [ROUTES.roleAssignments]: oimUrl('searchRoleAssignment'),
    [ROUTES.interventionPeriods]: oimUrl('searchInterventionPeriod'),
    [ROUTES.fieldVisibilities]: 'impact/secure/fieldVisibilities',
    [ROUTES.statistics]: DEFAULT_OIM_ROUTE,
    [ROUTES.localization.values]: DEFAULT_OIM_ROUTE,
    [ROUTES.localization.frontend]: DEFAULT_OIM_ROUTE,
    [ROUTES.localization.backend]: DEFAULT_OIM_ROUTE,
    [ROUTES.calendar]: DEFAULT_OIM_ROUTE,
    [ROUTES.notFound]: DEFAULT_OIM_ROUTE,
};

// This controls what routes are enabled on NIM (if a route is disabled on NIM then the OIM route will be used instead)
// - false: the route always redirects to OIM
// - true: the route uses NIM and doesn't redirect to OIM
// - FeatureFlag: the route uses NIM only if the specified flag is enabled, otherwise it redirects to OIM
export const ENABLED_NIM_ROUTES: Record<RouteType, FeatureFlag | boolean> = {
    [ROUTES.homepage]: true,
    [ROUTES.news]: false,
    [ROUTES.activity.search]: true,
    [ROUTES.activity.bulkActions]: true,
    [ROUTES.activity.watchlist]: FeatureFlag.V9,
    [ROUTES.activity.clusters]: false,
    [ROUTES.activity.scheduler]: false,
    [ROUTES.logbook]: false,
    [ROUTES.activity.create]: FeatureFlag.V9,
    [ROUTES.activity.view]: true,
    [ROUTES.activity.clone]: FeatureFlag.V9,
    [ROUTES.activity.createTemplate]: FeatureFlag.V9,
    [ROUTES.activity.cloneFromTemplate]: FeatureFlag.V9,
    [ROUTES.activity.changeRequest.view]: FeatureFlag.V9,
    [ROUTES.dimr.search]: true,
    [ROUTES.dimr.viewLatest]: true,
    [ROUTES.dimr.view]: true,
    [ROUTES.dimr.create]: true,
    [ROUTES.wdp.search]: true,
    [ROUTES.wdp.create]: true,
    [ROUTES.wdp.clone]: true,
    [ROUTES.wdp.view]: true,
    [ROUTES.wdp.viewLatest]: true,
    [ROUTES.vic.search]: true,
    [ROUTES.vic.create]: true,
    [ROUTES.vic.view]: true,
    [ROUTES.vic.clone]: true,
    [ROUTES.lockout.search]: true,
    [ROUTES.lockout.view]: true,
    [ROUTES.lockout.create]: true,
    [ROUTES.lockout.clone]: true,
    [ROUTES.firePermit.search]: true,
    [ROUTES.firePermit.view]: true,
    [ROUTES.firePermit.create]: true,
    [ROUTES.firePermit.clone]: true,
    [ROUTES.is37.search]: true,
    [ROUTES.is37.create]: true,
    [ROUTES.is37.view]: true,
    [ROUTES.is37.clone]: true,
    [ROUTES.noteDeCoupure.search]: true,
    [ROUTES.noteDeCoupure.create]: true,
    [ROUTES.noteDeCoupure.clone]: true,
    [ROUTES.noteDeCoupure.view]: true,
    [ROUTES.tasks]: true,
    [ROUTES.radiationDoses]: FeatureFlag.DOSE_REPORTS,
    [ROUTES.accessControl]: false,
    [ROUTES.locations]: false,
    [ROUTES.professionalVisits]: false,
    [ROUTES.roleAssignments]: false,
    [ROUTES.interventionPeriods]: false,
    [ROUTES.fieldVisibilities]: false,
    [ROUTES.statistics]: true,
    [ROUTES.localization.values]: true,
    [ROUTES.localization.frontend]: true,
    [ROUTES.localization.backend]: true,
    [ROUTES.calendar]: true,
    [ROUTES.notFound]: true,
};

const isRouteEnabled = (
    value: (typeof ENABLED_NIM_ROUTES)[keyof typeof ENABLED_NIM_ROUTES],
    features: Partial<Record<FeatureFlag, boolean>>
): boolean => (typeof value === 'boolean' ? value : !!features[value]);

const getOIMUrlFromRedirect = (
    to: RichRedirect,
    pathParams: Params,
    queryParams: ParsedQuery,
    origin: string,
    environment: EnvironmentDTO
): string => {
    const toResolved = getRoute(
        typeof to === 'string'
            ? { path: to, params: pathParams as { [key: string]: string } }
            : to(pathParams, queryParams)
    );
    const newOrigin = environment === 'PRODUCTION' ? origin.replace(/-ng/, '') : 'https://preprodimpact.cern.ch';
    return `${newOrigin}${toResolved}`;
};

export const getOIMUrl = (
    place: string | ((params: Params) => string),
    pathParams: Params,
    queryParams: ParsedQuery,
    origin: string,
    environment: EnvironmentDTO
): string => getOIMUrlFromRedirect(oimUrl(place), pathParams, queryParams, origin, environment);

export const findOIMRedirectionFor = (
    pathname: string,
    queryParams: ParsedQuery,
    origin: string,
    context: Pick<ApplicationContextDTO, 'environment' | 'features'>
): string | undefined => {
    const redirections = Object.entries(ROUTES_TO_OIM).reduce<string[]>((acc, [from, to]) => {
        const matched = matchPath(from, pathname);
        const routeEnabled = isRouteEnabled(ENABLED_NIM_ROUTES[from as keyof typeof ROUTES_TO_OIM], context.features);
        if (!routeEnabled && matched !== null) {
            return [...acc, getOIMUrlFromRedirect(to, matched.params, queryParams, origin, context.environment)];
        } else {
            return acc;
        }
    }, []);
    return redirections[0];
};

export const REDIRECT_NOT_IMPLEMENTED_TO_OIM: boolean = (() => {
    const rawValue = import.meta.env.VITE_REDIRECT_NOT_IMPLEMENTED_TO_OIM;
    if (rawValue === undefined) {
        return false;
    }
    const value = Object.fromEntries([false, true].map((b) => [String(b), b]))[rawValue];
    if (value === undefined) {
        throw new Error(
            `Incorrect value for environment variable VITE_REDIRECT_NOT_IMPLEMENTED_TO_OIM: ${String(rawValue)}`
        );
    }
    return value;
})();
